import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import {
  adminListGreenGiftProjects, adminCreateGreenGiftProject,
  adminUpdateGreenGiftProject, adminDeleteGreenGiftProject,
  adminUploadImage,
} from "@/lib/api";
import AdminHeader from "@/components/admin/AdminHeader";

const assetUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const emptyForm = {
  id: null, name: "", location: "", description: "",
  image: null, display_order: 100, status: "draft",
};

export default function AdminGreenGiftProjectsPage() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoadingItems(true);
    try { setItems(await adminListGreenGiftProjects()); }
    catch { toast.error("Failed to load projects"); }
    setLoadingItems(false);
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
    else if (user) load();
  }, [user, loading, navigate, load]);

  const startCreate = () => {
    // Auto-suggest next display_order.
    const maxOrder = items.reduce((m, x) => Math.max(m, x.display_order || 0), 0);
    setEditing({ ...emptyForm, display_order: maxOrder + 10 });
  };
  const startEdit = (a) => setEditing({ ...emptyForm, ...a });
  const cancel = () => setEditing(null);

  const doSave = async (statusOverride) => {
    if (!editing.name.trim()) { toast.error("Project name is required"); return; }
    const payload = {
      name: editing.name.trim(),
      location: editing.location || "",
      description: editing.description || "",
      image: editing.image || null,
      display_order: Number(editing.display_order) || 100,
      status: statusOverride || editing.status || "draft",
    };
    setSaving(true);
    try {
      if (editing.id) await adminUpdateGreenGiftProject(editing.id, payload);
      else await adminCreateGreenGiftProject(payload);
      toast.success(editing.id ? "Project updated" : "Project created");
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const onDelete = async (a) => {
    if (!window.confirm(`Delete project "${a.name}"?`)) return;
    try { await adminDeleteGreenGiftProject(a.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const move = async (proj, delta) => {
    // Swap display_order with the neighbour in the sorted list.
    const idx = items.findIndex((x) => x.id === proj.id);
    const target = items[idx + delta];
    if (!target) return;
    try {
      await Promise.all([
        adminUpdateGreenGiftProject(proj.id, {
          name: proj.name, location: proj.location || "", description: proj.description || "",
          image: proj.image || null, display_order: target.display_order, status: proj.status,
        }),
        adminUpdateGreenGiftProject(target.id, {
          name: target.name, location: target.location || "", description: target.description || "",
          image: target.image || null, display_order: proj.display_order, status: target.status,
        }),
      ]);
      load();
    } catch (e) { toast.error("Reorder failed"); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    try {
      const { url } = await adminUploadImage(file);
      setEditing((prev) => ({ ...prev, image: url }));
    } catch { toast.error("Upload failed"); }
  };

  if (loading || !user) return <div className="admin-shell"><div className="admin-loading">Loading…</div></div>;

  return (
    <div className="admin-shell utsah-app" data-testid="admin-ggp-page">
      <AdminHeader active="green-gift-projects" onLogout={async () => { await logout(); navigate("/admin/login"); }} email={user.email} />
      <div className="admin-container">
        <div className="admin-heading-row">
          <div>
            <div className="eyebrow">Green Gift</div>
            <h1 className="admin-heading">Projects</h1>
          </div>
          {!editing && (
            <button className="admin-export-btn" onClick={startCreate} data-testid="admin-new-ggp-btn">+ New Project</button>
          )}
        </div>

        {editing ? (
          <ProjectForm
            form={editing} saving={saving}
            onChange={setEditing} onSave={doSave} onCancel={cancel}
            onUploadImage={uploadImage}
          />
        ) : (
          <div className="admin-table-wrap" data-testid="admin-ggp-table">
            {loadingItems ? (
              <div className="admin-loading">Loading…</div>
            ) : items.length === 0 ? (
              <div className="admin-empty">No projects yet. Click "New Project" to create one.</div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr><th>Order</th><th>Name</th><th>Location</th><th>Status</th><th className="admin-th-actions">Actions</th></tr>
                </thead>
                <tbody>
                  {items.map((a, i) => (
                    <tr key={a.id} data-testid={`ggp-row-${a.id}`}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ minWidth: 32, fontWeight: 700 }}>{a.display_order}</span>
                          <button className="admin-link-btn" onClick={() => move(a, -1)} disabled={i === 0} title="Move up">↑</button>
                          <button className="admin-link-btn" onClick={() => move(a, 1)} disabled={i === items.length - 1} title="Move down">↓</button>
                        </div>
                      </td>
                      <td><strong>{a.name}</strong></td>
                      <td>{a.location || <span style={{ opacity: 0.4 }}>—</span>}</td>
                      <td><span className={`admin-badge admin-badge-${a.status}`}>{a.status}</span></td>
                      <td className="admin-td-actions">
                        <button className="admin-link-btn" onClick={() => startEdit(a)} data-testid={`ggp-edit-${a.id}`}>Edit</button>
                        <button className="admin-link-btn admin-link-danger" onClick={() => onDelete(a)} data-testid={`ggp-del-${a.id}`}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      <Toaster position="bottom-center" richColors theme="light" />
    </div>
  );
}

function ProjectForm({ form, saving, onChange, onSave, onCancel, onUploadImage }) {
  const set = (k, v) => onChange({ ...form, [k]: v });

  return (
    <div className="admin-form" data-testid="admin-ggp-form">
      <section className="admin-form-section">
        <h3 className="admin-form-section-title">1 · Project Details</h3>
        <div className="admin-form-row">
          <label>Project Name<span className="req">*</span></label>
          <input type="text" value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Native Grove Restoration"
            data-testid="ggp-form-name" />
        </div>
        <div className="admin-form-row">
          <label>Location</label>
          <input type="text" value={form.location}
            onChange={(e) => set("location", e.target.value)}
            placeholder="e.g. Nilgiris, Tamil Nadu"
            data-testid="ggp-form-location" />
        </div>
        <div className="admin-form-row">
          <label>Description</label>
          <textarea rows={5} value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Short paragraph shown beneath the project name"
            data-testid="ggp-form-description" />
        </div>
      </section>

      <section className="admin-form-section">
        <h3 className="admin-form-section-title">2 · Featured Image</h3>
        <div className="admin-form-row">
          <label>Upload Image</label>
          <input type="file" accept="image/*"
            onChange={(e) => onUploadImage(e.target.files?.[0])}
            data-testid="ggp-form-image-upload" />
          {form.image && (
            <div className="admin-image-tile" style={{ maxWidth: 320, marginTop: 12 }}>
              <img src={assetUrl(form.image)} alt="" />
              <div className="admin-image-controls">
                <button type="button" className="admin-link-danger"
                  onClick={() => set("image", null)}>Remove</button>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="admin-form-section">
        <h3 className="admin-form-section-title">3 · Display & Publishing</h3>
        <div className="admin-form-row">
          <label>Display Order (lower first)</label>
          <input type="number" min={0} value={form.display_order}
            onChange={(e) => set("display_order", e.target.value)}
            data-testid="ggp-form-order" />
        </div>
        <div className="admin-form-row">
          <label>Status</label>
          <select value={form.status}
            onChange={(e) => set("status", e.target.value)}
            data-testid="ggp-form-status">
            <option value="draft">Draft (hidden)</option>
            <option value="published">Published (live)</option>
          </select>
        </div>
      </section>

      <div className="admin-form-actions" data-testid="ggp-form-actions">
        <button className="admin-nav-btn" onClick={onCancel} disabled={saving} data-testid="ggp-form-cancel">Cancel</button>
        <button className="admin-nav-btn" onClick={() => onSave("draft")} disabled={saving} data-testid="ggp-form-save-draft">Save Draft</button>
        <button className="admin-export-btn" onClick={() => onSave("published")} disabled={saving} data-testid="ggp-form-publish">
          {saving ? "Saving…" : "Publish"}
        </button>
      </div>
    </div>
  );
}
